import { useMemo, useRef, useState, useEffect, useLayoutEffect, useCallback, useSyncExternalStore } from 'react';
import { DEFAULT_SETTINGS } from '@core/settings/schema.ts';
import { LocalStorageSettingsStore } from '../adapters/settings/LocalStorageSettingsStore.ts';
import {
  createSubtitleProvider,
  type CreateSubtitleProviderOptions,
  type SubtitleProviderKind,
} from '../adapters/subtitles/index.ts';
import { SubtitleOverlay } from './SubtitleOverlay.tsx';
import { CuePanel } from './CuePanel.tsx';
import { TrackSelector } from './TrackSelector.tsx';
import { LoginDialog } from './LoginDialog.tsx';
import { SettingsPanel } from './SettingsPanel.tsx';
import { useYoutubePlayer, type UseYoutubePlayerOptions } from '../hooks/useYoutubePlayer.ts';
import { useSubtitleSync } from '../hooks/useSubtitleSync.ts';
import { useSubtitleLoader } from '../hooks/useSubtitleLoader.ts';
import { usePlayerShortcuts } from '../hooks/usePlayerShortcuts.ts';
import { useStageRails } from '../hooks/useStageRails.ts';
import { StageRails } from './StageRails.tsx';
import { useSubtitleReader } from '../hooks/useSubtitleReader.ts';
import { useAuth } from '../hooks/useAuth.ts';
import { useTranslation } from '../hooks/useTranslation.ts';
import { useI18n, languageDisplayName } from '../i18n/index.ts';
import { useDeviceClass } from '../hooks/useDeviceClass.ts';
import { useInputCapabilities } from '../hooks/useInputCapabilities.ts';
import { usePinToVisualViewport } from '../hooks/usePinToVisualViewport.ts';
import { useContain16x9 } from '../hooks/useContain16x9.ts';
import { SubtitleStore } from '../state/subtitleStore.ts';
import { DictationInput } from '../features/dictation/DictationInput.tsx';
import { useDictation } from '../features/dictation/useDictation.ts';
import { useSpeaking } from '../features/speaking/useSpeaking.ts';
import { isDubbingTimingDragging } from '../../../extension/src/adapters/dom/dubbingTimingDrag.ts';
import { getSubtitlePositionDraggingSlot } from '../../../extension/src/adapters/dom/subtitlePositionDrag.ts';
import { useLearning } from '../features/learning/useLearning.ts';
import { LearningPopup } from '../features/learning/LearningPopup.tsx';
import { installLearningHoverPause } from '../features/learning/learningHoverPause.ts';
import {
  DEFAULT_SHORTCUT_BINDINGS,
  mergeShortcutBindings,
} from '@core/shortcuts/bindings.ts';
import { matchShortcut, isEditableKeyboardTarget } from '@core/shortcuts/matchShortcut.ts';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import type { SubtitleProvider } from '../ports/SubtitleProvider.ts';
import type { TranslationProvider } from '@ports/TranslationProvider.ts';
import type { AuthApiOptions } from '../adapters/auth/authApi.ts';

export type PlayerProps = {
  videoId: string;
  onBack: () => void;
  playerOptions?: UseYoutubePlayerOptions;
  /** Injected for tests; defaults to a module-owned store. */
  store?: SubtitleStore;
  settingsStore?: SettingsStore;
  /** Override initial provider (tests). Defaults to YouTube via Sora. */
  initialProviderKind?: SubtitleProviderKind;
  mockProviderOptions?: CreateSubtitleProviderOptions;
  /** Inject a ready-made provider (skips factory). */
  provider?: SubtitleProvider;
  /** Inject translation provider (tests). */
  translationProvider?: TranslationProvider;
  /** Auth API overrides (tests). */
  authOptions?: AuthApiOptions & { revalidateOnMount?: boolean };
  /** Navigate to cards page. */
  onOpenCards?: () => void;
  /** Navigate to Style Studio. */
  onOpenStyleStudio?: () => void;
};

/**
 * Fullscreen player shell: YT embed + subtitle overlay + track picker + translation + auth.
 */
export function Player({
  videoId,
  onBack,
  playerOptions,
  store: storeProp,
  settingsStore: settingsProp,
  initialProviderKind = 'sora',
  mockProviderOptions,
  provider: providerProp,
  translationProvider,
  authOptions,
  onOpenCards,
  onOpenStyleStudio,
}: PlayerProps) {
  const { containerRef, ready, error, controller } = useYoutubePlayer(videoId, playerOptions);
  const shellRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLDivElement>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  // While login is open, freeze the shell — VV resize on keyboard open/close
  // otherwise remounts focus and blocks paste on iOS.
  usePinToVisualViewport(shellRef, ready && !loginOpen);
  // layoutKey: remeasure when dictation keyboard-compose toggles top-dock padding.
  useContain16x9(frameRef, mediaRef, ready, composeOpen);
  const defaultStore = useRef(new SubtitleStore()).current;
  const defaultSettings = useRef(new LocalStorageSettingsStore({ host: null })).current;
  const store = storeProp ?? defaultStore;
  const settingsStore = settingsProp ?? defaultSettings;

  const [providerKind] = useState<SubtitleProviderKind>(initialProviderKind);
  const [panelOpen, setPanelOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [cuePanelOpen, setCuePanelOpen] = useState(false);
  const [readerEnabled, setReaderEnabled] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const { t } = useI18n();
  const { dim, device, orientation } = useDeviceClass();
  const { canHover } = useInputCapabilities();
  const authRef = useRef({ isMember: false, userId: '', userToken: '' });
  const ownedProvider = useMemo(() => {
    if (providerProp) return null;
    const delayRaw =
      typeof window !== 'undefined'
        ? Number(new URLSearchParams(window.location.search).get('subsDelay') ?? '')
        : NaN;
    const downloadDelayMs = Number.isFinite(delayRaw) && delayRaw > 0 ? delayRaw : undefined;
    return createSubtitleProvider(providerKind, {
      ...mockProviderOptions,
      ...(downloadDelayMs != null ? { downloadDelayMs } : {}),
      getAuth: () => authRef.current,
      shouldInjectCreatorEnhanced: () =>
        (settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS).subtitleOptimize === true,
      getOptimizeModel: () =>
        (settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS).subtitleOptimizeModel,
    });
  }, [providerProp, providerKind, mockProviderOptions, settingsStore]);
  const provider = providerProp ?? ownedProvider;

  const loader = useSubtitleLoader(provider, videoId, store, settingsStore);
  const overlayRootRef = useRef<HTMLDivElement>(null);
  const sync = useSubtitleSync(controller, store, settingsStore, {
    videoId,
    getOverlayRoot: () => overlayRootRef.current,
  });
  const onOverlayHostReady = useCallback(() => {
    sync.invalidate();
  }, [sync.invalidate]);
  const auth = useAuth(settingsStore, {
    revalidateOnMount: authOptions?.revalidateOnMount ?? true,
    ...authOptions,
  });
  authRef.current = {
    isMember: auth.unlock,
    userId: auth.userId,
    userToken: auth.userToken,
  };

  const learning = useLearning({
    settings: sync.settings,
    store,
    video: controller,
    unlock: auth.unlock,
    userId: auth.userId,
    userToken: auth.userToken,
  });

  useEffect(() => {
    if (!sync.settings.learningMode || !controller) return undefined;
    return installLearningHoverPause({
      getSettings: () => settingsStore.getSnapshot?.() ?? sync.settings,
      getVideo: () => controller,
      canHover,
    });
  }, [sync.settings.learningMode, controller, settingsStore, sync.settings, canHover]);
  const invalidateRef = useRef(sync.invalidate);
  invalidateRef.current = sync.invalidate;
  const onInvalidate = useCallback(() => {
    invalidateRef.current();
  }, []);

  useSubtitleReader({
    enabled: readerEnabled,
    main: sync.main,
    second: sync.second,
    settings: sync.settings,
    video: controller,
    store,
  });

  const dictation = useDictation({
    settings: sync.settings,
    video: controller,
    store,
    onInvalidate,
  });

  // Speaking / shadowing record / replay take (shortcuts + compose chrome).
  const speaking = useSpeaking({
    enabled:
      sync.settings.speakingMode || sync.settings.practiceMode === 'shadowing',
    settings: sync.settings,
    shortcutsEnabled: sync.settings.enableShortcuts,
  });

  usePlayerShortcuts({
    video: controller,
    store,
    settingsStore,
    rootRef: stageRef,
    ready,
  });

  const rails = useStageRails({
    video: controller,
    store,
    settingsStore,
    ready,
    dim,
    orientation,
    practiceMode: sync.settings.practiceMode,
    composeOpen,
    panelOpen,
    settingsOpen,
    loginOpen,
    onInvalidate,
  });

  // Shadowing: while pointer is over ticker cues/handle (or mid-drag), keep iframe
  // from stealing the gesture so center/timing drags win. Rails never block the
  // media box — YouTube stays tappable while controls float in letterbox bands.
  useEffect(() => {
    if (sync.settings.practiceMode !== 'shadowing') return undefined;
    const embed = containerRef.current;
    if (!embed) return undefined;

    let blocked = false;
    const setBlocked = (on: boolean) => {
      if (on === blocked) return;
      blocked = on;
      if (on) {
        embed.style.setProperty('pointer-events', 'none', 'important');
        for (const node of embed.querySelectorAll('iframe')) {
          (node as HTMLElement).style.setProperty('pointer-events', 'none', 'important');
        }
      } else {
        embed.style.removeProperty('pointer-events');
        for (const node of embed.querySelectorAll('iframe')) {
          (node as HTMLElement).style.removeProperty('pointer-events');
        }
      }
    };

    const overShadowingHit = (clientX: number, clientY: number) => {
      const el = document.elementFromPoint(clientX, clientY);
      if (!el) return false;
      return Boolean(
        el.closest('.unisubs-dubbing-cue')
        || el.closest('.unisubs-sub-drag-handle')
        || el.closest('#mainSub.unisubs-dubbing')
        || el.closest('#secondSub.unisubs-dubbing'),
      );
    };

    const syncBlock = (clientX: number, clientY: number) => {
      if (isDubbingTimingDragging() || getSubtitlePositionDraggingSlot()) {
        setBlocked(true);
        return;
      }
      setBlocked(overShadowingHit(clientX, clientY));
    };

    const onPointer = (event: PointerEvent) => {
      syncBlock(event.clientX, event.clientY);
    };
    const onEnd = (event: PointerEvent) => {
      // After drag release, re-evaluate under the pointer.
      queueMicrotask(() => syncBlock(event.clientX, event.clientY));
    };

    window.addEventListener('pointermove', onPointer, true);
    window.addEventListener('pointerdown', onPointer, true);
    window.addEventListener('pointerup', onEnd, true);
    window.addEventListener('pointercancel', onEnd, true);
    return () => {
      window.removeEventListener('pointermove', onPointer, true);
      window.removeEventListener('pointerdown', onPointer, true);
      window.removeEventListener('pointerup', onEnd, true);
      window.removeEventListener('pointercancel', onEnd, true);
      setBlocked(false);
    };
  }, [sync.settings.practiceMode, ready, videoId]);

  // Letterbox geometry: portrait rails sit in bands outside the 16:9 media box.
  const [mediaBox, setMediaBox] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    frameW: 0,
    frameH: 0,
  });
  useLayoutEffect(() => {
    if (!rails.eligible) return undefined;
    const frame = frameRef.current;
    const media = mediaRef.current;
    if (!frame || !media) return undefined;
    const syncBox = () => {
      const fr = frame.getBoundingClientRect();
      const mr = media.getBoundingClientRect();
      setMediaBox({
        top: mr.top - fr.top,
        left: mr.left - fr.left,
        width: mr.width,
        height: mr.height,
        frameW: fr.width,
        frameH: fr.height,
      });
    };
    syncBox();
    const ro = new ResizeObserver(syncBox);
    ro.observe(frame);
    ro.observe(media);
    window.addEventListener('resize', syncBox);
    window.visualViewport?.addEventListener('resize', syncBox);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', syncBox);
      window.visualViewport?.removeEventListener('resize', syncBox);
    };
  }, [rails.eligible, ready, orientation, dim, composeOpen]);

  useEffect(() => {
    if (!sync.settings.enableShortcuts) return undefined;
    const bindings = mergeShortcutBindings(
      sync.settings.shortcutBindings ?? DEFAULT_SHORTCUT_BINDINGS,
    );
    const onKeyDown = (e: KeyboardEvent) => {
      if (isEditableKeyboardTarget(e.target)) return;
      if (matchShortcut(e, bindings.reader)) {
        e.preventDefault();
        setReaderEnabled((v) => !v);
      }
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [sync.settings.enableShortcuts, sync.settings.shortcutBindings]);

  useEffect(() => {
    if (sync.settings.subtitleList) setCuePanelOpen(true);
    else setCuePanelOpen(false);
  }, [sync.settings.subtitleList]);

  const translation = useTranslation(
    controller,
    store,
    settingsStore,
    auth,
    {
      videoId,
      onInvalidate: () => {
        invalidateRef.current();
      },
      provider: translationProvider,
      memberOnlyText: t('translationMemberOnly'),
    },
  );

  const overlay = (
    <SubtitleOverlay
      hostRef={overlayRootRef}
      settings={sync.settings}
      settingsStore={settingsStore}
      video={controller}
      onHostReady={onOverlayHostReady}
      onInvalidate={() => {
        invalidateRef.current();
      }}
    />
  );

  const mainSlot = useSyncExternalStore(
    (cb) => store.subscribe(cb),
    () => store.getSlot('main'),
  );
  const rawTarget = sync.settings.mainTranslations[0] ?? '';
  const targetLang = rawTarget && rawTarget !== 'null' ? rawTarget : '';
  const hasMainCues = Boolean(mainSlot.cues?.length);
  const membershipError = Boolean(
    loader.error && /membership|member/i.test(loader.error),
  );
  // Source-only (no translation picked) is a valid watch mode — never block the stage.
  // Download / generate progress paints on the subtitle track (extension info cues).
  // Topbar chrome only for listing / hard errors / empty catalog.
  const showStageStatus =
    loader.listing
    || Boolean(loader.error)
    || loader.empty;

  const openTracks = useCallback(() => {
    setSettingsOpen(false);
    setCuePanelOpen(false);
    setLoginOpen(false);
    setPanelOpen((o) => !o);
    const more = document.querySelector<HTMLDetailsElement>('[data-testid="player-more"]');
    if (more) more.open = false;
  }, []);

  const openLogin = useCallback(() => {
    setPanelOpen(false);
    setSettingsOpen(false);
    setCuePanelOpen(false);
    setLoginOpen(true);
    const more = document.querySelector<HTMLDetailsElement>('[data-testid="player-more"]');
    if (more) more.open = false;
  }, []);

  const openSettings = useCallback(() => {
    setPanelOpen(false);
    setCuePanelOpen(false);
    setLoginOpen(false);
    setSettingsOpen((o) => !o);
    const more = document.querySelector<HTMLDetailsElement>('[data-testid="player-more"]');
    if (more) more.open = false;
  }, []);

  const openCues = useCallback(() => {
    setPanelOpen(false);
    setSettingsOpen(false);
    setLoginOpen(false);
    setCuePanelOpen((o) => !o);
    const more = document.querySelector<HTMLDetailsElement>('[data-testid="player-more"]');
    if (more) more.open = false;
  }, []);

  const [summaryVisible, setSummaryVisible] = useState(true);
  useEffect(() => {
    setSummaryVisible(true);
    const id = window.setTimeout(() => setSummaryVisible(false), 8000);
    return () => window.clearTimeout(id);
  }, [videoId, targetLang]);

  const watching = sync.settings.practiceMode === 'watch'
    || sync.settings.practiceMode === 'shadowing';
  const compactChrome = dim === 'D1' || dim === 'D2';

  return (
    <div
      ref={shellRef}
      className={`player${ready ? ' player--ready' : ''}${watching ? ' player--watch' : ' player--practice'}${composeOpen ? ' player--compose' : ''}${panelOpen || settingsOpen || cuePanelOpen || loginOpen ? ' player--overlay-open' : ''}${rails.eligible ? ' player--rails-eligible' : ''}${rails.pinned ? ' player--rails-pinned' : ''}`}
      data-testid="player-shell"
      data-ready={ready ? 'true' : 'false'}
      data-practice-mode={sync.settings.practiceMode}
      data-compose={composeOpen ? 'open' : 'closed'}
      data-compact-chrome={compactChrome ? 'true' : 'false'}
      data-dim={dim}
      data-device={device}
      data-orientation={orientation}
      data-rails={rails.eligible ? (rails.pinned ? 'pinned' : rails.visible ? 'open' : 'armed') : 'off'}
    >
      <div className="player__chrome">
        <header className="player__topbar">
          <button type="button" className="player__back" onClick={onBack} aria-label={t('back')}>
            {t('back')}
          </button>
          <div className="player__brand-cluster">
            {showStageStatus ? (
              <div
                className={`player__subtitle-load${loader.error || loader.empty ? ' player__subtitle-load--action' : ''}${loader.error ? ' player__subtitle-load--error' : ''}`}
                data-testid="subtitle-load-status"
                data-busy={loader.busy ? 'true' : 'false'}
                data-empty={loader.empty ? 'true' : 'false'}
                role={loader.error ? 'alert' : 'status'}
              >
                <p className="player__subtitle-load-msg">
                  {loader.error
                    ? loader.error
                    : loader.empty
                      ? t('trackEmpty')
                      : t('subtitleLoadListing')}
                </p>
                {loader.error || loader.empty ? (
                  <div className="player__subtitle-load-actions">
                    {loader.error ? (
                      <button
                        type="button"
                        className="player__subtitle-load-btn player__subtitle-load-btn--secondary"
                        data-testid="subtitle-load-dismiss"
                        onClick={() => loader.clearError()}
                      >
                        {t('stageDismiss')}
                      </button>
                    ) : null}
                    {loader.error ? (
                      <button
                        type="button"
                        className="player__subtitle-load-btn"
                        data-testid="subtitle-load-retry"
                        onClick={() => {
                          loader.clearError();
                          void loader.refreshTracks();
                        }}
                      >
                        {t('stageRetryLoad')}
                      </button>
                    ) : null}
                    {membershipError ? (
                      <button
                        type="button"
                        className="player__subtitle-load-btn"
                        data-testid="subtitle-load-signin"
                        onClick={openLogin}
                      >
                        {t('stageSignIn')}
                      </button>
                    ) : null}
                    {loader.empty || (loader.error && !membershipError) ? (
                      <button
                        type="button"
                        className="player__subtitle-load-btn"
                        data-testid={loader.empty ? 'subtitle-load-open-settings' : 'subtitle-load-open-tracks'}
                        onClick={loader.empty ? openSettings : openTracks}
                      >
                        {loader.empty ? t('stageOpenSettings') : t('stageOpenTracks')}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="player__topbar-actions">
          {!compactChrome ? (
            <>
              <button
                type="button"
                className="player__menu"
                data-testid="tracks-toggle"
                onClick={openTracks}
              >
                {t('tracks')}
              </button>
              <button
                type="button"
                className="player__menu"
                data-testid="settings-toggle"
                onClick={openSettings}
              >
                {t('settings')}
              </button>
              <button
                type="button"
                className="player__menu"
                data-testid="login-toggle"
                onClick={openLogin}
              >
                {auth.unlock ? t('member') : auth.userId ? t('account') : t('signIn')}
              </button>
              <button
                type="button"
                className="player__menu"
                data-testid="cue-list-toggle"
                onClick={openCues}
              >
                {t('cueList')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="player__menu"
                data-testid="tracks-toggle"
                onClick={openTracks}
              >
                {t('tracks')}
              </button>
              <details className="player__more" data-testid="player-more">
                <summary className="player__menu">
                  <span className="player__menu-label">{t('more')}</span>
                </summary>
                <div className="player__more-menu">
                  <button
                    type="button"
                    className="player__more-item"
                    data-testid="settings-toggle"
                    onClick={openSettings}
                  >
                    {t('settings')}
                  </button>
                  <button
                    type="button"
                    className="player__more-item"
                    data-testid="login-toggle"
                    onClick={openLogin}
                  >
                    {auth.unlock ? t('member') : auth.userId ? t('account') : t('signIn')}
                  </button>
                  <button
                    type="button"
                    className="player__more-item"
                    data-testid="cue-list-toggle"
                    onClick={openCues}
                  >
                    {t('cueList')}
                  </button>
                </div>
              </details>
            </>
          )}
          </div>
        </header>
      </div>

      <div
        ref={stageRef}
        className="player__stage"
        tabIndex={-1}
        data-testid="player-stage"
        onPointerDown={(e) => {
          // Do not steal focus from form fields / sheet panels — iOS long-press
          // paste otherwise loses the input (Style Studio works because it has no stage).
          const el = e.target as HTMLElement | null;
          if (
            el?.closest(
              'input, textarea, select, [contenteditable="true"], .settings-panel, .track-selector, .cue-panel, .login-dialog, .learning-popup',
            )
          ) {
            return;
          }
          rails.onStagePointerDown(e.target);
          if (el?.closest('.stage-rails')) return;
          stageRef.current?.focus();
        }}
      >
        {(panelOpen || settingsOpen || cuePanelOpen || loginOpen) && (
          <button
            type="button"
            className="player__scrim"
            data-testid="panel-scrim"
            aria-label={t('settingsClose')}
            onClick={() => {
              setPanelOpen(false);
              setSettingsOpen(false);
              setCuePanelOpen(false);
              setLoginOpen(false);
            }}
          />
        )}
        <div className="player__video" data-testid="player-video">
          {/*
            Frame = band below chrome / above practice pill.
            Media = max 16:9 box centered in that band (portrait + landscape).
          */}
          <div className="player__frame" data-testid="player-frame" ref={frameRef}>
            <div className="player__media" data-testid="player-media" ref={mediaRef}>
              <div
                ref={containerRef}
                className="player__embed"
                data-testid="yt-embed"
              />
              {ready ? overlay : null}
            </div>
            {rails.eligible && mediaBox.width > 0 ? (
              <div
                className="stage-rails-layer"
                data-testid="stage-rails-layer"
                data-layout={rails.layout}
                style={{
                  ['--media-top' as string]: `${mediaBox.top}px`,
                  ['--media-left' as string]: `${mediaBox.left}px`,
                  ['--media-width' as string]: `${mediaBox.width}px`,
                  ['--media-height' as string]: `${mediaBox.height}px`,
                  ['--media-bottom' as string]: `${mediaBox.top + mediaBox.height}px`,
                  ['--media-right' as string]: `${mediaBox.left + mediaBox.width}px`,
                }}
              >
                {/*
                  While rails are hidden, a full-frame reveal catches taps
                  (including over the video). Cross-origin YouTube cannot receive
                  that same gesture, so we also toggle playback here — otherwise
                  play/pause would need a second tap after rails appear.
                  Once visible, the overlay unmounts and YouTube is tappable
                  again until autohide. Dictation/speaking never mount this.
                  Learning token taps and shadowing cue/handle drags still work
                  with this reveal in place.
                */}
                {!rails.visible ? (
                  <button
                    type="button"
                    className="stage-rails-reveal"
                    data-testid="stage-rails-reveal"
                    aria-label={t('railControls')}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      rails.showRails();
                      rails.togglePlayback();
                    }}
                  />
                ) : null}
                <StageRails
                  visible={rails.visible}
                  toolsMode={rails.toolsMode}
                  pinned={rails.pinned}
                  layout={rails.layout}
                  subY={rails.subY}
                  fontSize={rails.fontSize}
                  centerOffset={rails.centerOffset}
                  showCenterControl={rails.showCenterControl}
                  paused={rails.paused}
                  onSeekPrev={rails.seekPrev}
                  onSeekRepeat={rails.seekRepeat}
                  onSeekNext={rails.seekNext}
                  onTogglePlayback={rails.togglePlayback}
                  mainDelayMs={rails.mainDelayMs}
                  secondDelayMs={rails.secondDelayMs}
                  onToggleDelayMainMode={rails.toggleDelayMainMode}
                  onToggleDelaySecondMode={rails.toggleDelaySecondMode}
                  onNudgeDelayMain={rails.nudgeDelayMain}
                  onNudgeDelaySecond={rails.nudgeDelaySecond}
                  onTogglePositionMode={rails.togglePositionMode}
                  onToggleFontSizeMode={rails.toggleFontSizeMode}
                  onToggleCenterMode={rails.toggleCenterMode}
                  onTogglePin={rails.togglePin}
                  onSubYChange={rails.setSubY}
                  onFontSizeChange={rails.setFontSize}
                  onCenterOffsetChange={rails.setCenterOffset}
                  onInteract={rails.onInteract}
                  labels={{
                    seekPrev: t('railSeekPrev'),
                    seekRepeat: t('railSeekRepeat'),
                    seekNext: t('railSeekNext'),
                    play: t('railPlay'),
                    pause: t('railPause'),
                    delayMain: t('railDelayMain'),
                    delaySecond: t('railDelaySecond'),
                    delayDown: t('railDelayDown'),
                    delayUp: t('railDelayUp'),
                    position: t('railPosition'),
                    positionSlider: t('railPositionSlider'),
                    fontSize: t('railFontSize'),
                    fontSizeSlider: t('railFontSizeSlider'),
                    center: t('railCenter'),
                    centerSlider: t('railCenterSlider'),
                    pin: t('railPin'),
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
        {ready && learning.popup ? (
          <LearningPopup
            key={`${learning.popup.slot}:${learning.popup.selectedText}`}
            selectedText={learning.popup.selectedText}
            slot={learning.popup.slot}
            clientX={learning.popup.clientX}
            clientY={learning.popup.clientY}
            settings={sync.settings}
            store={store}
            video={controller}
            unlock={auth.unlock}
            userId={auth.userId}
            userToken={auth.userToken}
            decks={learning.decks}
            deckId={learning.selectedDeckId}
            onDeckChange={learning.setSelectedDeckId}
            onClose={learning.closePopup}
          />
        ) : null}
        {/* Dictation lives on stage (above chrome). */}
        {ready
        && !showStageStatus
        && targetLang
        && hasMainCues
        && watching
        && summaryVisible ? (
          <button
            type="button"
            className="player__bilingual-chip"
            data-testid="bilingual-summary-chip"
            onClick={openTracks}
          >
            {t('stageBilingualSummary').replace(
              '{lang}',
              languageDisplayName(targetLang),
            )}
          </button>
        ) : null}
        {!ready
        && (sync.settings.practiceMode === 'dictation'
          || sync.settings.practiceMode === 'speaking'
          || sync.settings.practiceMode === 'learning') ? (
          <p
            className="player__practice-waiting"
            data-testid="practice-waiting-player"
            role="status"
          >
            {t('practiceWaitingPlayer')}
          </p>
        ) : null}
        {ready && translation.status === 'error' ? (
          <p
            className="player__translation-status"
            data-testid="translation-status"
            data-status={translation.status}
          >
            {t('translationFailed')}
          </p>
        ) : null}
        {ready ? (
          <DictationInput
            settings={sync.settings}
            dictation={dictation}
            speaking={speaking}
            overlayHostRef={overlayRootRef}
            onComposeOpenChange={setComposeOpen}
          />
        ) : null}
        {!ready && !error ? <p className="player__status">Loading player…</p> : null}
        {error ? (
          <p className="player__status player__status--error" role="alert">
            {error}
          </p>
        ) : null}
        <TrackSelector
          open={panelOpen}
          onClose={() => setPanelOpen(false)}
          store={store}
          settingsStore={settingsStore}
          loader={loader}
          unlock={auth.unlock}
          onRequestLogin={() => {
            setPanelOpen(false);
            setSettingsOpen(false);
            setCuePanelOpen(false);
            setLoginOpen(true);
          }}
        />
        <LoginDialog auth={auth} open={loginOpen} onClose={() => setLoginOpen(false)} />
        <SettingsPanel
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          settingsStore={settingsStore}
          store={store}
          videoId={videoId}
          unlock={auth.unlock}
          onImportFile={(slot, file) => loader.importFile(slot, file, t('importSrtUploaded'))}
          readerEnabled={readerEnabled}
          onReaderEnabledChange={setReaderEnabled}
          onOpenCards={onOpenCards}
          onOpenStyleStudio={onOpenStyleStudio}
        />
        <CuePanel
          open={cuePanelOpen}
          onClose={() => setCuePanelOpen(false)}
          store={store}
          settingsStore={settingsStore}
          video={controller}
        />
      </div>
    </div>
  );
}
